/**
 * Хук для получения системных алертов из бэкенда
 */
import { useQuery } from '@tanstack/react-query';

import api from '../shared/api/client';
import { isNetworkError, TIMEOUTS } from '../shared/config/constants';
import { QK } from '../shared/model/queryKeys';
import {
  humanizeAlertDescription,
  humanizeAlertSource,
  humanizeAlertTitle,
} from '../shared/notifications';

export interface Alert {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  source: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
  acknowledged?: boolean;
  acknowledged_by?: string;
  acknowledged_at?: string;
}

export interface AlertsResponse {
  status: string;
  total: number;
  shown: number;
  alerts: Alert[];
  note?: string;
}

interface RawAlertPayload {
  fingerprint?: unknown;
  status?: unknown;
  labels?: Record<string, unknown>;
  annotations?: Record<string, unknown>;
  startsAt?: unknown;
  updatedAt?: unknown;
  id?: unknown;
  severity?: unknown;
  title?: unknown;
  description?: unknown;
  source?: unknown;
  timestamp?: unknown;
  metadata?: unknown;
}

interface RawAlertsResponse {
  status?: unknown;
  total?: unknown;
  shown?: unknown;
  alerts?: unknown;
  note?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function normalizeSeverity(alert: RawAlertPayload): Alert['severity'] {
  const labels = isRecord(alert.labels) ? alert.labels : {};
  const rawSeverity = String(alert.severity ?? labels.severity ?? '').toLowerCase();
  const rawState = isRecord(alert.status)
    ? String(alert.status.state ?? '').toLowerCase()
    : String(alert.status ?? '').toLowerCase();

  if (rawSeverity.includes('critical') || rawSeverity.includes('error')) return 'critical';
  if (rawSeverity.includes('warning') || rawSeverity.includes('warn')) return 'warning';
  // severity=none — служебные алерты (Watchdog горит всегда): не повод звонить в колокольчик.
  if (rawSeverity === 'none') return 'info';
  if (rawState === 'firing') return 'warning';
  return 'info';
}

export function normalizeAlert(rawAlert: unknown, index: number): Alert {
  const alert = isRecord(rawAlert) ? (rawAlert as RawAlertPayload) : {};
  const labels = isRecord(alert.labels) ? alert.labels : {};
  const annotations = isRecord(alert.annotations) ? alert.annotations : {};
  const severity = normalizeSeverity(alert);
  const alertName = readString(labels.alertname);
  const sourceRaw =
    readString(alert.source)
    ?? readString(annotations.source)
    ?? readString(labels.service)
    ?? readString(labels.job)
    ?? readString(labels.instance)
    ?? readString(labels.component)
    ?? readString(labels.container);
  const descriptionRaw =
    readString(alert.description)
    ?? readString(annotations.description)
    ?? readString(annotations.message)
    ?? readString(annotations.summary);
  const title = humanizeAlertTitle(readString(alert.title) ?? readString(annotations.summary), alertName);
  const source = humanizeAlertSource(sourceRaw);
  const timestamp =
    readString(alert.timestamp)
    ?? readString(alert.startsAt)
    ?? readString(alert.updatedAt)
    ?? new Date().toISOString();
  const stableId =
    readString(alert.id)
    ?? readString(alert.fingerprint)
    ?? `${timestamp}:${alertName ?? 'alert'}:${source}:${index}`;

  return {
    id: stableId,
    severity,
    title,
    description: humanizeAlertDescription(descriptionRaw, severity),
    source,
    timestamp,
    acknowledged: false,
    ...(isRecord(alert.metadata) ? { metadata: alert.metadata } : {}),
  };
}

export const useAlerts = (limit = 10) => {
  return useQuery<AlertsResponse>({
    queryKey: QK.alerts(limit),
    queryFn: async () => {
      const response = await api.get<RawAlertsResponse>('/api/ws/metrics/alerts', {
        params: { limit },
      });
      const data = response.data;
      const rawAlerts = Array.isArray(data.alerts) ? data.alerts : [];
      const normalizedAlerts = rawAlerts.map((item, index) => normalizeAlert(item, index));
      const total = typeof data.total === 'number' ? data.total : normalizedAlerts.length;
      const shown = typeof data.shown === 'number' ? data.shown : normalizedAlerts.length;

      const result: AlertsResponse = {
        status: readString(data.status) ?? 'ok',
        total,
        shown,
        alerts: normalizedAlerts,
      };
      const note = readString(data.note);
      return note ? { ...result, note } : result;
    },
    staleTime: TIMEOUTS.CACHE_SHORT,
    refetchInterval: 30000,
    retry: (failureCount, error) => (isNetworkError(error) ? false : failureCount < 2),
    retryDelay: (failureCount, error) => (isNetworkError(error) ? 0 : Math.min(3000 * 2 ** failureCount, 15000)),
  });
};

